'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../utils/cn';

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  className?: string;
};

/**
 * Select field following the flat/glass design system. The browser's native
 * dropdown arrow is hidden and replaced with a custom chevron spaced comfortably
 * from the right border (the native arrow hugs the edge). `className` is applied to
 * the wrapper (so width/margin utilities work); the inner <select> is full-width.
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div className={cn('relative w-full', className)}>
        <select
          ref={ref}
          className={cn(
            // Hide the native arrow; leave room on the right for our own chevron.
            'appearance-none flex h-10 w-full rounded-xl glass-input pl-3 pr-9 py-2',
            'text-sm font-regular text-foreground cursor-pointer',
            'hover:glass-surface-hover',
            'focus-visible:outline-none focus-visible:glass-input-focus',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          strokeWidth={2.5}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70"
        />
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
